/**
 * @fileoverview TimeTrackerProvider — app-wide time capture state (MOR-906)
 *
 * Purpose:
 * - Owns the running stopwatch so it survives navigation between routes.
 *   Previously the timer lived inside `WeeklyTimesheet`, which meant that
 *   starting a timer and navigating away left it running with no visible
 *   way to stop it.
 * - Owns the open/prefill state for the global quick time entry dialog so
 *   any surface (header button, keyboard shortcut, future per-job CTAs)
 *   can log time without prop drilling.
 *
 * Mounted once in the dashboard layout, alongside `CommandPaletteProvider`.
 *
 * Dependencies:
 * - localStorage (timer survives reloads and stays in step across tabs)
 */

import { useCallback, useEffect, useState, type ReactNode } from "react";
import {
  TimeTrackerContext,
  type QuickEntryPrefill,
} from "./TimeTrackerContext";

/** Shared with the pre-provider implementation so an in-flight timer survives the upgrade. */
const TIMER_STORAGE_KEY = "morgan-timesheet-timer-start";

interface RunningTimer {
  /** Epoch milliseconds the stopwatch was started. */
  startedAt: number;
  /** Matter the user was viewing when they started, used to prefill the dialog. */
  jobId?: string;
}

function readStoredTimer(): RunningTimer | null {
  try {
    const raw = localStorage.getItem(TIMER_STORAGE_KEY);
    if (!raw) return null;
    // Legacy format was a bare epoch-milliseconds number.
    const asNumber = Number(raw);
    if (Number.isFinite(asNumber) && asNumber > 0) {
      return { startedAt: asNumber };
    }
    const parsed = JSON.parse(raw) as Partial<RunningTimer> | null;
    return typeof parsed?.startedAt === "number"
      ? { startedAt: parsed.startedAt, jobId: parsed.jobId }
      : null;
  } catch {
    return null;
  }
}

export function TimeTrackerProvider({ children }: { children: ReactNode }) {
  const [timer, setTimer] = useState<RunningTimer | null>(readStoredTimer);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [quickEntryOpen, setQuickEntryOpen] = useState(false);
  const [quickEntryPrefill, setQuickEntryPrefill] =
    useState<QuickEntryPrefill | null>(null);
  const [quickEntryQueue, setQuickEntryQueue] = useState<QuickEntryPrefill[]>(
    [],
  );
  const [nlEntryOpen, setNlEntryOpen] = useState(false);

  useEffect(() => {
    if (!timer) return;
    setNowMs(Date.now());
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [timer]);

  // Two tabs sharing one stopwatch would otherwise drift apart, which is
  // very visible now that the timer sits in the always-on-screen header.
  useEffect(() => {
    function onStorage(event: StorageEvent) {
      if (event.key === TIMER_STORAGE_KEY) setTimer(readStoredTimer());
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const startTimer = useCallback((jobId?: string) => {
    const next: RunningTimer = { startedAt: Date.now(), jobId };
    localStorage.setItem(TIMER_STORAGE_KEY, JSON.stringify(next));
    setTimer(next);
  }, []);

  const discardTimer = useCallback(() => {
    localStorage.removeItem(TIMER_STORAGE_KEY);
    setTimer(null);
  }, []);

  const stopTimer = useCallback(() => {
    if (!timer) return null;
    const stoppedAt = Date.now();
    localStorage.removeItem(TIMER_STORAGE_KEY);
    setTimer(null);
    return {
      elapsedMinutes: (stoppedAt - timer.startedAt) / 60_000,
      jobId: timer.jobId,
      startedAt: timer.startedAt,
      stoppedAt,
    };
  }, [timer]);

  const openQuickEntry = useCallback((prefill?: QuickEntryPrefill) => {
    setQuickEntryPrefill(prefill ?? null);
    setQuickEntryQueue([]);
    setQuickEntryOpen(true);
  }, []);

  const openQuickEntryBatch = useCallback((prefills: QuickEntryPrefill[]) => {
    if (prefills.length === 0) return;
    const [first, ...rest] = prefills;
    setQuickEntryPrefill(first);
    setQuickEntryQueue(rest);
    setQuickEntryOpen(true);
  }, []);

  const advanceQuickEntry = useCallback(() => {
    if (quickEntryQueue.length === 0) return false;
    const [next, ...rest] = quickEntryQueue;
    setQuickEntryPrefill(next);
    setQuickEntryQueue(rest);
    return true;
  }, [quickEntryQueue]);

  const closeQuickEntry = useCallback(() => {
    setQuickEntryOpen(false);
    setQuickEntryQueue([]);
  }, []);

  const openNlEntry = useCallback(() => setNlEntryOpen(true), []);
  const closeNlEntry = useCallback(() => setNlEntryOpen(false), []);

  const elapsedSec = timer
    ? Math.max(0, Math.floor((nowMs - timer.startedAt) / 1000))
    : 0;

  return (
    <TimeTrackerContext.Provider
      value={{
        timer,
        elapsedSec,
        startTimer,
        stopTimer,
        discardTimer,
        quickEntryOpen,
        quickEntryPrefill,
        openQuickEntry,
        openQuickEntryBatch,
        quickEntryRemaining: quickEntryQueue.length,
        advanceQuickEntry,
        closeQuickEntry,
        nlEntryOpen,
        openNlEntry,
        closeNlEntry,
      }}
    >
      {children}
    </TimeTrackerContext.Provider>
  );
}
